import {
    all,
    call,
    getContext,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects';
import {
    getOperations as getOperationsAction,
    setOperations,
    setSelectedOperation,
    setOperationInterlocutor,
    getInvoiceNotif,
    setActiveProducer,
} from '../../redux/actions';
import {
    GET_OPERATION_PARTICIPANTS,
    GET_OPERATION_INTERLOCUTOR,
    GET_OPERATIONS,
    UPDATE_OPERATION,
    UPDATE_OPERATION_THEME,
} from '../../redux/reducers/constants';
import i18n from 'i18next';
import { selectUser } from '../../redux/selectors/authenticationSelectors';
import { selectRoutes } from '../../redux/selectors/routeSelector';
import { dsoEnum, roles, routesConstants } from '../../../../app-config';
import { createUrl } from '../../../adapters/primary/libs/createUrl';
import { errorHandler } from '../common/errorHandler';

function* getOperations(action) {
    try {
        let operations;
        const operationGateway = yield getContext('operationGateway');
        // action dispatched from backoffice
        if (action && action.role === 'admin') {
            operations = yield call(operationGateway.getAllOperations);
        } else {
            const { role } = yield select(selectUser);
            switch (role) {
                case 'participant':
                    const participants = yield call(
                        operationGateway.getParticipantsByJwt
                    );
                    const operationsIds = [
                        ...new Set(
                            participants.map((part) => part.operationId)
                        ),
                    ];
                    operations = yield call(
                        operationGateway.getOperations,
                        operationsIds
                    );
                    operations.forEach((operation) => {
                        operation.producers = participants.filter(
                            (part) => part.operationId === operation.id
                        );
                    });
                    break;
                case 'observer':
                    operations = yield call(
                        operationGateway.getObserverOperationsWithDetailNumbers
                    );
                    break;
                case 'organizer':
                    const organizer = yield call(
                        operationGateway.getOrganizerByJwt
                    );
                    operations = yield call(
                        operationGateway.getOrganizerOperationsWithDetailNumbers,
                        organizer.id
                    );
                    break;
                default:
            }
            if (i18n.isInitialized) {
                if (
                    operations.map((op) => op.dso).includes(dsoEnum.SIBELGA) &&
                    i18n.language !== 'be_NL'
                ) {
                    i18n.changeLanguage('be_FR');
                } else if (
                    !operations.map((op) => op.dso).includes(dsoEnum.SIBELGA)
                ) {
                    i18n.changeLanguage('fr');
                }
            }
            const operationsWithLogo = operations.filter(
                (op) => op.theme && op.theme.logo
            );
            yield call(operationGateway.getPresignedLogos, operationsWithLogo);
        }
        yield put(setOperations(operations));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getOperationParticipantsAndObservers(action) {
    try {
        const operationGateway = yield getContext('operationGateway');
        const measurementGateway = yield getContext('measurementGateway');
        const { operation, dateFrom, dateTo } = action;
        if (operation) {
            const { role } = (yield select(selectUser)) || {};
            const participantsAndObserversCalls = [
                call(
                    operationGateway.getOperationParticipants,
                    operation.id,
                    dateFrom,
                    dateTo
                ),
            ];
            if (role === roles.ORGANIZER) {
                participantsAndObserversCalls.push(
                    call(operationGateway.getOperationObservers, operation.id)
                );
            }
            const [participants, observers] = yield all(
                participantsAndObserversCalls
            );
            operation.observers = observers || [];
            const { history } = (yield select(selectRoutes)) || {};
            if (role !== roles.PARTICIPANT) {
                operation.producers = [];
                operation.consumers = [];
                participants.forEach((participant) =>
                    (participant.energyType === 'consumer'
                        ? operation.consumers
                        : operation.producers
                    ).push(participant)
                );
            } else {
                let { uploadedData } = yield call(
                    measurementGateway.getDateRanges,
                    operation.id,
                    operation.producers.map((prod) => prod.id)
                );
                operation.activeProducerId =
                    uploadedData.length > 0
                        ? uploadedData.reduce((acc, curr) =>
                              acc.to > curr.to ? acc : curr
                          ).participantId
                        : operation.producers[0].id;
                yield put(setActiveProducer(operation?.activeProducerId));
                history.push(
                    createUrl(
                        routesConstants.ENOAPP,
                        `operations/${operation.id}/dashboard/prod/${operation.activeProducerId}`
                    )
                );
                operation.consumers = participants.filter(
                    (participant) => participant.energyType === 'consumer'
                );
            }
            yield put(getInvoiceNotif(operation?.id));
            yield put(setSelectedOperation(operation));
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* updateOperation(action) {
    try {
        const { operation, formData } = action;
        const operationGateway = yield getContext('operationGateway');
        const updateCalls = [];
        updateCalls.push(call(operationGateway.updateOperation, operation));
        if (formData) {
            updateCalls.push(
                call(operationGateway.uploadLogo, operation, formData)
            );
        }
        yield all(updateCalls);
        yield put(getOperationsAction());
    } catch (error) {
        yield errorHandler(error);
    }
}

function* updateOperationTheme(action) {
    try {
        const { operation, theme, formData } = action;
        const operationGateway = yield getContext('operationGateway');
        const updateCalls = [];
        updateCalls.push(
            call(operationGateway.updateOperationTheme, operation.id, theme)
        );
        if (formData) {
            updateCalls.push(
                call(operationGateway.uploadLogo, operation, formData)
            );
        }
        yield all(updateCalls);
        yield put(getOperationsAction());
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getOperationInterlocutor(action) {
    try {
        const operationGateway = yield getContext('operationGateway');
        const { operation } = action;
        const interlocutor = yield call(
            operationGateway.getOperationInterlocutor,
            operation.id
        );
        operation.interlocutor = interlocutor;
        yield put(setOperationInterlocutor(interlocutor));
    } catch (error) {
        yield errorHandler(error);
    }
}

export function* getOperationsSaga() {
    yield takeLatest(GET_OPERATIONS, getOperations);
}

export function* getOperationParticipantsSaga() {
    yield takeLatest(
        GET_OPERATION_PARTICIPANTS,
        getOperationParticipantsAndObservers
    );
}

export function* updateOperationSaga() {
    yield takeLatest(UPDATE_OPERATION, updateOperation);
}

export function* updateOperationThemeSaga() {
    yield takeLatest(UPDATE_OPERATION_THEME, updateOperationTheme);
}

export function* getOperationInterlocutorSaga() {
    yield takeLatest(GET_OPERATION_INTERLOCUTOR, getOperationInterlocutor);
}

const operationSagas = [
    getOperationsSaga,
    getOperationParticipantsSaga,
    getOperationInterlocutorSaga,
    updateOperationSaga,
    updateOperationThemeSaga,
];

export default operationSagas;
